import {
    EpisodeStatus,
    VisitDatetimeField,
    VisitOutcome,
    VisitState,
} from '@axle-health/graphql-schema';

import { PENN_CONTAINER_KEYS } from '../containerKeys';
import { addDays, date, dateInDays, datetime } from '../graphInputHelpers';
import { BuilderContext, FilterInputByEntity, QueueDefinition, RootEntity } from '../types';
import { DEFAULT_CONTAINER_FILTER_WINDOW_DAYS } from './pinnacleServiceIds';

/**
 * Penn Medicine at Home.
 *
 * Penn's queues read services by name rather than by external ID, so every list below
 * holds the service names exactly as Penn's database spells them. A name that does not
 * match a row matches no visits, and nothing warns about it, so the lists are
 * transcribed verbatim from the org's own service list rather than tidied.
 */

/** Services that open an episode. */
const SOC_SERVICE_NAMES = [
    'OT Non-OASIS Start of Care',
    'OT Non-OASIS SOC/No Further Vt',
    'OT OASIS SOC',
    'Outpt Home OT Start of Care',
    'Outpt Home PT Start of Care',
    'Outpt Home SLP Start of Care',
    'PT Non-OASIS Start of Care',
    'PT Non-OASIS SOC/No Further Vt',
    'PT OASIS SOC/No Further Visits',
    'PT OASIS SOC',
    'SLP Non-OASIS SOC',
    'SLP Non-OASIS SOC/No Further Visits',
    'SLP OASIS SOC/No Further Vsts',
    'SLP OASIS SOC',
    'SN Non-OASIS SOC/Therapy Cont.',
    'SN Non-OASIS SOC',
    'SN Non-OASIS SOC/No Further',
    'SN OASIS SOC/NO FURTHER VISITS',
    'SN OASIS SOC/Therapy Cont.',
    'SN OASIS Start of Care',
];

/** Services that resume an episode, including combined resumption and recert. */
const ROC_SERVICE_NAMES = [
    'OT Non-OASIS ROC',
    'OT Non-OASIS ROC/RECERT',
    'OT OASIS ROC',
    'OT OASIS ROC/RECERT',
    'PT Non-OASIS ROC',
    'PT Non-OASIS ROC/RECERT',
    'PT OASIS ROC',
    'PT OASIS ROC/Recert',
    'SLP Non-OASIS ROC',
    'SLP Non-OASIS ROC/RECERT',
    'SLP OASIS ROC',
    'SLP OASIS ROC/Recert',
    'SN Non-OASIS ROC',
    'SN Non-OASIS ROC/RECERT',
    'SN OASIS ROC',
    'SN OASIS ROC/Recert',
];

/** Services that evaluate a patient into a discipline. */
const EVAL_SERVICE_NAMES = [
    'Lymphedema Therapy Consult Visit',
    'MSW Initial Visit',
    'MSW Initial Visit Before SOC',
    'OT Initial Eval Before SOC',
    'OT Initial Evaluation',
    'Outpatient Home OT Evaluation',
    'Outpatient Home PT Evaluation',
    'Outpatient Home SLP Evaluation',
];

/**
 * Services that discharge a patient from the agency.
 *
 * Penn's spec also names an "SNF at home discharge", which has no service by that name
 * in their database, so no queue can gate on it.
 */
const OASIS_DISCHARGE_SERVICE_NAMES = [
    'OT Non-OASIS SOC/No Further Vt',
    'OT Non-OASIS Agency Discharge',
    'OT OASIS Agency Discharge',
    'OT OASIS Agency DC w/TFA',
    'PT OASIS SOC/No Further Visits',
    'SLP Non-OASIS SOC/No Further Visits',
    'SLP Non-OASIS Agency Discharge',
    'SLP OASIS Agency DC W/TFA',
    'SLP OASIS SOC/No Further Vsts',
    'SLP OASIS Agency Discharge',
    'SN OASIS SOC/NO FURTHER VISITS',
    'SN OASIS Agency DC',
    'SN Non-OASIS Agency DC',
    'Outpatient Home SLP Agency DC',
    'Outpatient Home PT Agency DC',
    'Outpatient Home OT Agency DC',
];

/** Services that recertify an episode. */
const OASIS_RECERT_SERVICE_NAMES = [
    'OT Non-OASIS Recert',
    'OT Non-OASIS Recert W/HHA SUP',
    'Outpatient Home OT Agency DC',
    'Outpatient Home OT Recert',
    'Outpatient Home PT Agency DC',
    'Outpatient Home PT Recert',
];

/** Services that close out an episode, by recert or by discharge. */
const RECERT_OR_DISCHARGE_SERVICE_NAMES = [
    'OT Non-OASIS Recert',
    'OT Non-OASIS Recert W/HHA SUP',
    'Outpatient Home OT Agency DC',
    'Outpatient Home OT Recert',
    'Outpatient Home PT Agency DC',
    'Outpatient Home PT Recert',
];

/** Services that discharge a patient from one discipline. */
const OT_DISCIPLINE_DISCHARGE_SERVICE_NAMES = [
    'OT Discipline Discharge',
    'OT Discipline Discharge w/TFA',
    'Outpt Home OT Discipline DC',
];
const PT_DISCIPLINE_DISCHARGE_SERVICE_NAMES = [
    'PT Discipline Discharge',
    'PT Discipline Discharge w/TFA',
    'Outpt Home PT Discipline DC',
];
const SN_DISCIPLINE_DISCHARGE_SERVICE_NAMES = ['SN Discipline Discharge'];
const ST_DISCIPLINE_DISCHARGE_SERVICE_NAMES = [
    'SLP Discipline Discharge',
    'SLP Discipline Discharge w/TFA',
    'Outpt Home SLP Discipline DC',
];
const MSW_DISCIPLINE_DISCHARGE_SERVICE_NAMES = ['MSW Discipline Discharge'];

const ALL_DISCIPLINE_DISCHARGE_SERVICE_NAMES = [
    ...OT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...PT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...SN_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...ST_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ...MSW_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
];

/**
 * Aide visits, and the supervisory visits that have to cover them.
 *
 * Penn's spec also names aide case communication, missed visits and telephone
 * encounters as anchors, none of which exist as services in their database.
 */
const HHA_VISIT_SERVICE_NAMES = ['Aide Home Visit', 'Aide Hospice Visit'];
const HHA_SUPERVISORY_SERVICE_NAMES = [
    'OT Home Visit W/HHA Sup',
    'PT Home Visit w/HHA SUP',
    'SLP Home Visit / HHA SUP',
    'SN Home Visit W/HHA SUP',
];

/**
 * Therapy-assistant visits, and the supervisory visits that cover them.
 *
 * Penn has no assistant visit service to anchor on yet, so the therapy supervisory
 * queue matches nothing until one is named.
 */
const PTA_VISIT_SERVICE_NAMES: string[] = [];
const PT_THERAPY_SUPERVISORY_SERVICE_NAMES = ['PT PTA Supervisory Visit'];

/** Services that satisfy a therapy reassessment. */
const REASSESSMENT_SERVICE_NAMES = ['OT Reassessment', 'PT Reassessment', 'SLP Reassessment'];

const OVERDUE_GRACE_MS = 15 * 60 * 1000;

type TherapyDiscipline = 'PT' | 'OT' | 'ST';

const THERAPY_DISCIPLINE_DISCHARGE_SERVICE_NAMES: Record<TherapyDiscipline, string[]> = {
    PT: PT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    OT: OT_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
    ST: ST_DISCIPLINE_DISCHARGE_SERVICE_NAMES,
};

/**
 * An upcoming discharge in one discipline, either agency-wide or discipline-only.
 *
 * The supervisory and re-eval queues use it to drop patients already on their way
 * out, who need no further coverage in that discipline.
 */
const pendingDischargeVisitFilter = (
    discipline: TherapyDiscipline,
    { now, timezone }: BuilderContext,
): FilterInputByEntity[RootEntity.VISIT] => ({
    services: {
        name: {
            inList: [
                ...OASIS_DISCHARGE_SERVICE_NAMES,
                ...THERAPY_DISCIPLINE_DISCHARGE_SERVICE_NAMES[discipline],
            ],
        },
    },
    disciplines: { code: { exact: discipline } },
    outcome: { exact: VisitOutcome.NONE },
    effectiveDate: { gte: date(now, timezone) },
});

/**
 * One discipline's arm of the therapy re-eval queue.
 *
 * A patient qualifies on an upcoming visit in the discipline, then drops out on any
 * of three grounds: a reassessment completed inside the look-back, one already
 * booked and fully assigned close enough behind a prior reassessment to count, or a
 * discharge in that discipline already scheduled. Three of these are ORed together,
 * so one uncovered discipline is enough to surface the patient.
 */
const pendingReEvalBranch = (
    discipline: TherapyDiscipline,
    ctx: BuilderContext,
): FilterInputByEntity[RootEntity.PATIENT] => {
    const { now, timezone } = ctx;
    return {
        isActive: { exact: true },
        hasEpisodes: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
        },
        hasVisits: {
            disciplines: { code: { exact: discipline } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: date(now, timezone) },
        },
        AND: {
            NOT: {
                hasVisits: {
                    services: { name: { inList: REASSESSMENT_SERVICE_NAMES } },
                    disciplines: { code: { exact: discipline } },
                    outcome: { exact: VisitOutcome.COMPLETED },
                    effectiveDate: { gte: dateInDays(now, timezone, -25) },
                },
            },
            AND: {
                NOT: {
                    hasVisits: {
                        services: { name: { inList: REASSESSMENT_SERVICE_NAMES } },
                        disciplines: { code: { exact: discipline } },
                        outcome: { exact: VisitOutcome.NONE },
                        effectiveDate: { gte: date(now, timezone) },
                        needsAssignment: { exact: false },
                        relatedVisitCondition: {
                            exists: true,
                            samePatient: true,
                            filter: {
                                services: { name: { inList: REASSESSMENT_SERVICE_NAMES } },
                                disciplines: { code: { exact: discipline } },
                            },
                            timeConstraints: [
                                {
                                    field: VisitDatetimeField.EFFECTIVE_DATE,
                                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                    gteOffsetMinutes: -30 * 24 * 60,
                                    lteOffsetMinutes: 0,
                                },
                            ],
                        },
                    },
                },
                AND: {
                    NOT: { hasVisits: pendingDischargeVisitFilter(discipline, ctx) },
                },
            },
        },
    };
};

/**
 * One arm of a pending-supervisory queue.
 *
 * A patient qualifies on an upcoming anchor visit — the aide or assistant visit that
 * has to be supervised — then drops out on a supervisory visit completed inside the
 * look-back, or one already booked and fully assigned close enough behind a prior
 * one to count. Passing a discipline gates both exclusions to it and adds the
 * pending-discharge exclusion, which the agency-wide aide queue does not want.
 */
const pendingSupervisoryBranch = (
    {
        anchorServiceNames,
        supervisoryServiceNames,
        lookbackDays,
        lookaheadDays,
        discipline,
    }: {
        anchorServiceNames: string[];
        supervisoryServiceNames: string[];
        lookbackDays: number;
        lookaheadDays: number;
        discipline?: Extract<TherapyDiscipline, 'PT' | 'OT'>;
    },
    ctx: BuilderContext,
): FilterInputByEntity[RootEntity.PATIENT] => {
    const { now, timezone } = ctx;
    const disciplineFilter = discipline ? { disciplines: { code: { exact: discipline } } } : {};

    return {
        isActive: { exact: true },
        hasVisits: {
            services: { name: { inList: anchorServiceNames } },
            outcome: { exact: VisitOutcome.NONE },
            effectiveDate: { gte: date(now, timezone) },
        },
        NOT: {
            hasVisits: {
                services: { name: { inList: supervisoryServiceNames } },
                ...disciplineFilter,
                outcome: { exact: VisitOutcome.COMPLETED },
                effectiveDate: { gte: dateInDays(now, timezone, -lookbackDays) },
            },
        },
        AND: {
            NOT: {
                hasVisits: {
                    services: { name: { inList: supervisoryServiceNames } },
                    ...disciplineFilter,
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: date(now, timezone) },
                    needsAssignment: { exact: false },
                    relatedVisitCondition: {
                        exists: true,
                        samePatient: true,
                        sameEpisode: false,
                        filter: {
                            services: { name: { inList: supervisoryServiceNames } },
                            ...disciplineFilter,
                        },
                        timeConstraints: [
                            {
                                field: VisitDatetimeField.EFFECTIVE_DATE,
                                relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                                gteOffsetMinutes: -lookaheadDays * 24 * 60,
                                lteOffsetMinutes: 0,
                            },
                        ],
                    },
                },
            },
            ...(discipline
                ? { AND: { NOT: { hasVisits: pendingDischargeVisitFilter(discipline, ctx) } } }
                : {}),
        },
    };
};

// Active patients holding a pending visit in a discipline their care team does not
// cover. Miscellaneous-discipline visits are ignored, having no discipline to staff.
const INCOMPLETE_CARE_TEAM: QueueDefinition<RootEntity.PATIENT> = {
    key: PENN_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        isActive: { exact: true },
        hasEpisodes: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
            endDate: { gte: date(now, timezone) },
            hasVisits: {
                disciplineCoveredByCareTeam: { exact: false },
                outcome: { exact: VisitOutcome.NONE },
                NOT: {
                    disciplines: {
                        externalIds: { value: { exact: 'MISC' } },
                    },
                },
            },
        },
    }),
};

// Start of care visits on a live episode that still need a clinician or a date.
const SOC: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.soc,
    label: 'SOC (Start of Care)',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: SOC_SERVICE_NAMES } },
        needsAssignment: { exact: true },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Resumption of care visits that still need a clinician or a date. Gated on an
// on-hold episode as well as an active one, since a resumption is what ends the hold.
const ROC: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.roc,
    label: 'ROC (Resumption of Care)',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: ROC_SERVICE_NAMES } },
        needsAssignment: { exact: true },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.ON_HOLD] },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Eval visits on a live episode that still need a clinician or a date.
const EVAL: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.eval,
    label: 'Eval',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        services: { name: { inList: EVAL_SERVICE_NAMES } },
        needsAssignment: { exact: true },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
            endDate: { gte: date(now, timezone) },
        },
    }),
};

// Visits held in review because the patient did not show, which a scheduler has to
// either rebook or mark as missed.
const POTENTIAL_RESCHEDULES: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.potentialReschedules,
    label: 'Potential Reschedules',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now }) => ({
        patient: { isActive: { exact: true } },
        startDatetime: {
            gte: datetime(addDays(now, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS)),
        },
        state: { exact: VisitState.IN_REVIEW },
        outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
    }),
};

// Unassigned visits on a live episode whose window closes within the week.
const URGENT_VISITS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.urgentVisitsNeedingScheduling,
    label: 'Urgent Visits Needing Scheduling',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        episode: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
        },
        needsAssignment: { exact: true },
        latestEndDatetime: {
            gte: date(now, timezone),
            lte: dateInDays(now, timezone, 7),
        },
    }),
};

// Live episodes holding a visit that still needs a service, clinician or date.
const PATIENTS_NEEDING_SCHEDULING: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PENN_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        status: { exact: EpisodeStatus.ACTIVE },
        isActiveByDate: { exact: true },
        hasVisits: {
            needsAssignment: { exact: true },
        },
    }),
};

// Upcoming visits still on the books for a patient whose episode is on hold.
const TRANSFERRED_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.transferredPatientsWithPendingVisits,
    label: 'Transferred Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: date(now, timezone) },
        episode: { status: { exact: EpisodeStatus.ON_HOLD } },
    }),
};

// Upcoming visits still on the books for a patient the agency has discharged.
const AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.agencyDischargedPatientsWithPendingVisits,
    label: 'Agency Discharged Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: false } },
        outcome: { exact: VisitOutcome.NONE },
        effectiveDate: { gte: date(now, timezone) },
    }),
};

// Pending visits left behind in a discipline the patient has been discharged from.
const DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.disciplineDischargePatientsWithPendingVisits,
    label: 'Discipline Discharge Patients with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.NONE },
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            sameDiscipline: true,
            filter: {
                services: { name: { inList: ALL_DISCIPLINE_DISCHARGE_SERVICE_NAMES } },
                outcome: { exact: VisitOutcome.COMPLETED },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    ltOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Pending visits generated by an order that has since been discontinued.
const DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.discontinuedFrequenciesWithPendingVisits,
    label: 'Discontinued Frequencies with Pending Visits',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        patient: { isActive: { exact: true } },
        outcome: { exact: VisitOutcome.NONE },
        relatedOrder: {
            discontinuedAt: { lt: date(now, timezone) },
        },
    }),
};

// Pending visits on a live episode assigned to a clinician who is no longer active.
const INACTIVE_CLINICIAN_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: ({ now, timezone }) => ({
        outcome: { exact: VisitOutcome.NONE },
        assignedClinicians: { isActive: { exact: false } },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
            endDate: { gte: date(now, timezone) },
        },
        effectiveDate: {
            gte: dateInDays(now, timezone, -DEFAULT_CONTAINER_FILTER_WINDOW_DAYS),
        },
    }),
};

// Patients with an upcoming aide visit and no supervisory visit to cover it.
const PENDING_HHA_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: PENN_CONTAINER_KEYS.pendingHhaSupervisory,
    label: 'Pending HHA Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: (ctx) => ({
        hasEpisodes: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
        },
        ...pendingSupervisoryBranch(
            {
                anchorServiceNames: HHA_VISIT_SERVICE_NAMES,
                supervisoryServiceNames: HHA_SUPERVISORY_SERVICE_NAMES,
                lookbackDays: 5,
                lookaheadDays: 5,
            },
            ctx,
        ),
    }),
};

// Patients with an upcoming PT-assistant visit and no PT supervisory visit to cover it.
const PENDING_THERAPY_SUPERVISORY: QueueDefinition<RootEntity.PATIENT> = {
    key: PENN_CONTAINER_KEYS.pendingTherapySupervisory,
    label: 'Pending Therapy Supervisory Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: (ctx) => ({
        ...pendingSupervisoryBranch(
            {
                anchorServiceNames: PTA_VISIT_SERVICE_NAMES,
                supervisoryServiceNames: PT_THERAPY_SUPERVISORY_SERVICE_NAMES,
                lookbackDays: 25,
                lookaheadDays: 5,
                discipline: 'PT',
            },
            ctx,
        ),
    }),
};

// Patients due a therapy reassessment in any of PT, OT or ST.
const PENDING_THERAPY_REEVALUATION: QueueDefinition<RootEntity.PATIENT> = {
    key: PENN_CONTAINER_KEYS.pendingTherapyReevaluation,
    label: 'Pending Therapy Re-Evaluation Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: (ctx) => ({
        ...pendingReEvalBranch('PT', ctx),
        OR: {
            ...pendingReEvalBranch('OT', ctx),
            OR: {
                ...pendingReEvalBranch('ST', ctx),
            },
        },
    }),
};

// Episodes ending within ten days, including ones already past their end date, with
// nothing scheduled to close them out: no recert or discharge at all, and no recent
// resumption that already extended care.
const UPCOMING_END_OF_EPISODE: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PENN_CONTAINER_KEYS.upcomingEndOfEpisode,
    label: 'Upcoming End of Episode',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        status: { exact: EpisodeStatus.ACTIVE },
        patient: { isActive: { exact: true } },
        endDate: { lte: dateInDays(now, timezone, 10) },
        NOT: {
            hasVisits: {
                services: { name: { inList: RECERT_OR_DISCHARGE_SERVICE_NAMES } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            },
        },
        AND: {
            NOT: {
                hasVisits: {
                    services: { name: { inList: ROC_SERVICE_NAMES } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    effectiveDate: {
                        gte: dateInDays(now, timezone, -5),
                        lte: dateInDays(now, timezone, 10),
                    },
                },
            },
        },
    }),
};

// Visits of any service sitting ahead of the same patient's upcoming start of care.
const VISITS_SCHEDULED_BEFORE_SOC: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.visitsScheduledBeforeSoc,
    label: 'Visits Scheduled Before SOC',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now, timezone }) => ({
        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
        episode: {
            status: { inList: [EpisodeStatus.ACTIVE, EpisodeStatus.PENDING] },
            endDate: { gte: date(now, timezone) },
        },
        relatedVisitCondition: {
            exists: true,
            samePatient: true,
            filter: {
                services: { name: { inList: SOC_SERVICE_NAMES } },
                outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                effectiveDate: { gte: date(now, timezone) },
            },
            timeConstraints: [
                {
                    field: VisitDatetimeField.EFFECTIVE_DATE,
                    relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                    gtOffsetMinutes: 0,
                },
            ],
        },
    }),
};

// Live episodes carrying more than one discharge or recert visit. Only one belongs
// on an episode, so a second means one of them has to be resolved.
const CONFLICTING_DISCHARGE_AND_RECERT_VISITS: QueueDefinition<RootEntity.EPISODE_OF_CARE> = {
    key: PENN_CONTAINER_KEYS.conflictingDischargeAndRecertVisits,
    label: 'Conflicting Discharge and Recert Visits',
    rootEntity: RootEntity.EPISODE_OF_CARE,
    snoozable: true,
    heavyQuery: false,
    buildFilters: () => ({
        status: { exact: EpisodeStatus.ACTIVE },
        isActiveByDate: { exact: true },
        hasVisits: {
            services: {
                name: {
                    inList: [...OASIS_DISCHARGE_SERVICE_NAMES, ...OASIS_RECERT_SERVICE_NAMES],
                },
            },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            relatedVisitCondition: {
                exists: true,
                sameEpisode: true,
                filter: {
                    services: {
                        name: {
                            inList: [
                                ...OASIS_DISCHARGE_SERVICE_NAMES,
                                ...OASIS_RECERT_SERVICE_NAMES,
                            ],
                        },
                    },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                },
            },
        },
    }),
};

// Active patients on a live episode with nothing pending on the books.
const ACTIVE_PATIENTS_WITH_NO_VISITS: QueueDefinition<RootEntity.PATIENT> = {
    key: PENN_CONTAINER_KEYS.activePatientsWithNoVisits,
    label: 'Active Patients with No Visits',
    rootEntity: RootEntity.PATIENT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        isActive: { exact: true },
        hasEpisodes: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
        },
        NOT: {
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
            },
        },
    }),
};

// Completed start-of-care, resumption and eval visits with no order behind them to
// bill against.
const COMPLETED_VISITS_MISSING_AN_ORDER: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.completedVisitsMissingAnOrder,
    label: 'Completed Visits Missing an Order',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: true,
    buildFilters: () => ({
        outcome: { exact: VisitOutcome.COMPLETED },
        hasOrder: { exact: false },
        patient: { isActive: { exact: true } },
        episode: {
            status: { exact: EpisodeStatus.ACTIVE },
            isActiveByDate: { exact: true },
        },
        services: {
            name: {
                inList: [...SOC_SERVICE_NAMES, ...ROC_SERVICE_NAMES, ...EVAL_SERVICE_NAMES],
            },
        },
    }),
};

// Scheduled or confirmed visits whose start time has passed by more than the grace
// period, so nobody has started them.
const OVERDUE_VISITS: QueueDefinition<RootEntity.VISIT> = {
    key: PENN_CONTAINER_KEYS.overdueVisits,
    label: 'Overdue',
    rootEntity: RootEntity.VISIT,
    snoozable: true,
    heavyQuery: false,
    buildFilters: ({ now }) => ({
        state: { inList: [VisitState.SCHEDULED, VisitState.CONFIRMED] },
        startDatetime: { lt: datetime(new Date(now.getTime() - OVERDUE_GRACE_MS)) },
    }),
};

export const PENN_QUEUES: QueueDefinition[] = [
    INCOMPLETE_CARE_TEAM,
    SOC,
    ROC,
    EVAL,
    OVERDUE_VISITS,
    POTENTIAL_RESCHEDULES,
    URGENT_VISITS_NEEDING_SCHEDULING,
    PATIENTS_NEEDING_SCHEDULING,
    TRANSFERRED_PATIENTS_WITH_PENDING_VISITS,
    AGENCY_DISCHARGED_PATIENTS_WITH_PENDING_VISITS,
    DISCIPLINE_DISCHARGE_PATIENTS_WITH_PENDING_VISITS,
    DISCONTINUED_FREQUENCIES_WITH_PENDING_VISITS,
    INACTIVE_CLINICIAN_VISITS,
    PENDING_HHA_SUPERVISORY,
    PENDING_THERAPY_SUPERVISORY,
    PENDING_THERAPY_REEVALUATION,
    UPCOMING_END_OF_EPISODE,
    VISITS_SCHEDULED_BEFORE_SOC,
    CONFLICTING_DISCHARGE_AND_RECERT_VISITS,
    ACTIVE_PATIENTS_WITH_NO_VISITS,
    COMPLETED_VISITS_MISSING_AN_ORDER,
];
